import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

const LIMIT = 12;

function formatIso(raw, t) {
  if (!raw) return t("na");
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  } catch {
    return raw;
  }
}

const safeText = (v, fallback) => {
  const s = String(v ?? "").trim();
  return s ? s : fallback;
};

const firstNonEmptyText = (...values) => {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
};

const getSubmissionUsername = (submission) =>
  firstNonEmptyText(
    submission?.username,
    submission?.user_name,
    submission?.user,
    submission?.client_username
  );

const getSubmissionQrValue = (submission) =>
  firstNonEmptyText(submission?.qr_id, submission?.qr_code);

// ===== Main =====
const RepportPage = () => {
  const { t } = useTranslation();

  const API = process.env.REACT_APP_API_URL;

  const [application, setApplication] = useState("");

  // view mode
  const [tab, setTab] = useState("manage"); // "manage" | "history"

  // ---------- MANAGE STATES ----------
  const [titles, setTitles] = useState([]);
  const [loadingTitles, setLoadingTitles] = useState(false);
  const [titlesError, setTitlesError] = useState("");

  const [openTitle, setOpenTitle] = useState(null);
  const [subtitles, setSubtitles] = useState([]);
  const [loadingSubtitles, setLoadingSubtitles] = useState(false);
  const [subtitlesError, setSubtitlesError] = useState("");

  const [openSubtitle, setOpenSubtitle] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questionsError, setQuestionsError] = useState("");

  const [newTitle, setNewTitle] = useState("");
  const [addTitleMessage, setAddTitleMessage] = useState({ type: "", text: "" });
  const [addTitleLoading, setAddTitleLoading] = useState(false);

  const [newSubtitle, setNewSubtitle] = useState("");
  const [addSubtitleMessage, setAddSubtitleMessage] = useState({ type: "", text: "" });
  const [addSubtitleLoading, setAddSubtitleLoading] = useState(false);

  const [newQuestionText, setNewQuestionText] = useState("");
  const [newQuestionType, setNewQuestionType] = useState("open");
  const [newQuestionRequired, setNewQuestionRequired] = useState(false);
  const [newQuestionOptionsText, setNewQuestionOptionsText] = useState("");
  const [addQuestionMessage, setAddQuestionMessage] = useState({ type: "", text: "" });
  const [showAddQuestionForm, setShowAddQuestionForm] = useState(false);

  // ---------- HISTORY STATES ----------
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [history, setHistory] = useState([]);
  const [pageHist, setPageHist] = useState(0);
  const [historyQuery, setHistoryQuery] = useState("");

  // modal report (simple details like first version)
  const [detailModal, setDetailModal] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const [reportBuilt, setReportBuilt] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const reportPdfRef = useRef(null);

  // ---------- INIT ----------
  useEffect(() => {
    const storedApp = localStorage.getItem("userApplication");
    if (storedApp) setApplication(storedApp);
  }, []);

  // ============= API (MANAGE) =============
  const fetchTitles = useCallback(async () => {
    if (!application || !application.trim()) return;

    setLoadingTitles(true);
    setTitlesError("");
    try {
      const res = await axios.get(`${API}/api/repport/titles`, {
        params: { application },
      });
      setTitles(res.data?.data?.titles || []);
    } catch (err) {
      setTitlesError(err?.response?.data?.message || err.message || t("repport_err_titles_load"));
    } finally {
      setLoadingTitles(false);
    }
  }, [API, application, t]);

  const fetchSubtitles = useCallback(
    async (title) => {
      if (!application || !application.trim() || !title) return;

      setLoadingSubtitles(true);
      setSubtitlesError("");
      try {
        const res = await axios.get(`${API}/api/repport/subtitles`, {
          params: { application, title },
        });
        setSubtitles(res.data?.data?.subtitles || []);
      } catch (err) {
        if (err?.response?.status === 404) {
          setSubtitles([]);
          setSubtitlesError(t("repport_no_subtitles"));
        } else {
          setSubtitlesError(
            err?.response?.data?.message || err.message || t("repport_err_subtitles_load")
          );
        }
      } finally {
        setLoadingSubtitles(false);
      }
    },
    [API, application, t]
  );

  const fetchQuestions = useCallback(
    async (title, subtitle) => {
      if (!application || !application.trim() || !title || !subtitle) return;

      setLoadingQuestions(true);
      setQuestionsError("");
      try {
        const res = await axios.get(`${API}/api/repport/questions`, {
          params: { application, title, subtitle },
        });
        setQuestions(res.data?.data?.questions || []);
      } catch (err) {
        if (err?.response?.status === 404) {
          setQuestions([]);
          setQuestionsError(t("repport_no_questions"));
        } else {
          setQuestionsError(
            err?.response?.data?.message || err.message || t("repport_err_questions_load")
          );
        }
      } finally {
        setLoadingQuestions(false);
      }
    },
    [API, application, t]
  );

  // reset views when app changes
  useEffect(() => {
    if (!application || !application.trim()) return;

    // reset manage view
    fetchTitles();
    setOpenTitle(null);
    setSubtitles([]);
    setOpenSubtitle(null);
    setQuestions([]);
    setShowAddQuestionForm(false);

    // reset history view
    setHistory([]);
    setPageHist(0);
  }, [application, fetchTitles]);

  useEffect(() => {
    setPageHist(0);
  }, [historyQuery]);

  // ============= HANDLERS (MANAGE) =============
  const handleAddTitle = async () => {
    setAddTitleMessage({ type: "", text: "" });

    if (!application.trim()) return setAddTitleMessage({ type: "error", text: t("repport_err_app_missing") });
    if (!newTitle.trim()) return setAddTitleMessage({ type: "error", text: t("repport_err_title_required") });

    setAddTitleLoading(true);
    try {
      const payload = { application, title: newTitle.trim(), subtitle: "" };
      const res = await axios.post(`${API}/api/repport`, payload);
      const created = res.data?.data;

      if (created?.title && !titles.includes(created.title)) {
        setTitles((prev) => [...prev, created.title].sort());
      }
      setNewTitle("");
      setAddTitleMessage({ type: "success", text: t("repport_title_added") });
    } catch (err) {
      if (err?.response?.status === 409) {
        setAddTitleMessage({ type: "error", text: t("repport_err_title_exists") });
      } else {
        setAddTitleMessage({
          type: "error",
          text: `${t("repport_err_title_add_failed")} ${err?.response?.data?.message || err.message}`,
        });
      }
    } finally {
      setAddTitleLoading(false);
    }
  };

  const toggleTitleOpen = (title) => {
    if (openTitle === title) {
      setOpenTitle(null);
      setSubtitles([]);
      setOpenSubtitle(null);
      setQuestions([]);
      setSubtitlesError("");
      setQuestionsError("");
      setShowAddQuestionForm(false);
      setAddSubtitleMessage({ type: "", text: "" });
      setAddQuestionMessage({ type: "", text: "" });
      return;
    }
    setOpenTitle(title);
    setOpenSubtitle(null);
    setQuestions([]);
    fetchSubtitles(title);
    setAddSubtitleMessage({ type: "", text: "" });
    setAddQuestionMessage({ type: "", text: "" });
    setShowAddQuestionForm(false);
  };

  const handleDeleteTitle = async (title) => {
    if (!window.confirm(t("repport_confirm_delete_title", { title }))) return;
    try {
      await axios.delete(`${API}/api/repport`, { data: { application, title } });
      setTitles((prev) => prev.filter((tt) => tt !== title));
      if (openTitle === title) {
        setOpenTitle(null);
        setSubtitles([]);
        setOpenSubtitle(null);
        setQuestions([]);
        setShowAddQuestionForm(false);
      }
    } catch (err) {
      alert(`${t("repport_err_title_delete_failed")} ${err?.response?.data?.message || err.message}`);
    }
  };

  const handleAddSubtitle = async () => {
    setAddSubtitleMessage({ type: "", text: "" });

    if (!openTitle) return setAddSubtitleMessage({ type: "error", text: t("repport_err_select_title_first") });
    if (!newSubtitle.trim())
      return setAddSubtitleMessage({ type: "error", text: t("repport_err_subtitle_required") });

    setAddSubtitleLoading(true);
    try {
      const payload = { application, title: openTitle, subtitle: newSubtitle.trim() };
      await axios.post(`${API}/api/repport`, payload);
      setSubtitles((prev) =>
        [...prev, newSubtitle.trim()].filter((v, i, a) => a.indexOf(v) === i)
      );
      setNewSubtitle("");
      setAddSubtitleMessage({ type: "success", text: t("repport_subtitle_added") });
    } catch (err) {
      if (err?.response?.status === 409) {
        setAddSubtitleMessage({ type: "error", text: t("repport_err_subtitle_exists") });
      } else {
        setAddSubtitleMessage({
          type: "error",
          text: `${t("repport_err_subtitle_add_failed")} ${err?.response?.data?.message || err.message}`,
        });
      }
    } finally {
      setAddSubtitleLoading(false);
    }
  };

  const handleDeleteSubtitle = async (subtitle) => {
    if (!window.confirm(t("repport_confirm_delete_subtitle", { subtitle }))) return;
    try {
      await axios.delete(`${API}/api/repport`, {
        data: { application, title: openTitle, subtitle },
      });
      setSubtitles((prev) => prev.filter((s) => s !== subtitle));
      if (openSubtitle === subtitle) {
        setOpenSubtitle(null);
        setQuestions([]);
        setShowAddQuestionForm(false);
      }
    } catch (err) {
      alert(`${t("repport_err_subtitle_delete_failed")} ${err?.response?.data?.message || err.message}`);
    }
  };

  const toggleSubtitleOpen = (subtitle) => {
    if (openSubtitle === subtitle) {
      setOpenSubtitle(null);
      setQuestions([]);
      setQuestionsError("");
      setAddQuestionMessage({ type: "", text: "" });
      setShowAddQuestionForm(false);
      return;
    }
    setOpenSubtitle(subtitle);
    fetchQuestions(openTitle, subtitle);
    setAddQuestionMessage({ type: "", text: "" });
    setShowAddQuestionForm(false);
  };

  const handleAddQuestion = async () => {
    setAddQuestionMessage({ type: "", text: "" });

    if (!openTitle || !openSubtitle)
      return setAddQuestionMessage({ type: "error", text: t("repport_err_select_title_subtitle_first") });

    if (!newQuestionText.trim())
      return setAddQuestionMessage({ type: "error", text: t("repport_err_question_text_required") });

    if (!["open", "qcm", "yes_no"].includes(newQuestionType))
      return setAddQuestionMessage({ type: "error", text: t("repport_err_invalid_question_type") });

    let options;
    if (newQuestionType === "qcm") {
      options = newQuestionOptionsText
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      if (!options.length)
        return setAddQuestionMessage({ type: "error", text: t("repport_err_qcm_options_required") });
    }

    try {
      const payload = {
        application,
        title: openTitle,
        subtitle: openSubtitle,
        question_text: newQuestionText.trim(),
        question_type: newQuestionType,
        is_required: newQuestionRequired,
      };
      if (options) payload.options = options;

      await axios.post(`${API}/api/repport/questions`, payload);
      await fetchQuestions(openTitle, openSubtitle);

      setNewQuestionText("");
      setNewQuestionType("open");
      setNewQuestionRequired(false);
      setNewQuestionOptionsText("");
      setAddQuestionMessage({ type: "success", text: t("repport_question_added") });
      setShowAddQuestionForm(false);
    } catch (err) {
      setAddQuestionMessage({
        type: "error",
        text: `${t("repport_err_question_add_failed")} ${err?.response?.data?.message || err.message}`,
      });
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm(t("repport_confirm_delete_question"))) return;
    try {
      await axios.delete(`${API}/api/repport/questions/${questionId}`);
      setQuestions((prev) => prev.filter((q) => q.id !== questionId));
    } catch (err) {
      alert(`${t("repport_err_question_delete_failed")} ${err?.response?.data?.message || err.message}`);
    }
  };

  // ============= HISTORY =============
  const fetchHistoryAll = useCallback(async () => {
    if (!application || !application.trim()) return;

    setHistoryLoading(true);
    setHistoryError("");
    try {
      const res = await axios.get(`${API}/api/repport/history/all`, {
        params: { application },
      });
      setHistory(res.data?.data?.submissions || []);
      setPageHist(0);
    } catch (err) {
      setHistoryError(err?.response?.data?.message || err.message || t("repport_err_history_load"));
    } finally {
      setHistoryLoading(false);
    }
  }, [API, application, t]);

  const buildReportForSubmission = async (submission) => {
    setReportError("");
    setReportLoading(true);
    setReportBuilt([]);

    try {
      const answers = submission?.answers || {};
      const questionIds = Object.keys(answers)
        .map((k) => parseInt(k, 10))
        .filter((n) => !Number.isNaN(n));

      if (!questionIds.length) {
        setReportBuilt([]);
        return;
      }

      const metaRes = await axios.get(`${API}/api/repport/meta/by-question-ids`, {
        params: { question_ids: questionIds.join(",") },
      });
      const rows = metaRes.data?.data || [];

      const groupsMap = new Map();
      for (const r of rows) {
        const key = `${r.application}|||${r.title}|||${r.subtitle}`;
        if (!groupsMap.has(key)) {
          groupsMap.set(key, {
            application: r.application,
            title: r.title,
            subtitle: r.subtitle,
            questions: [],
          });
        }
      }

      const groupKeys = Array.from(groupsMap.keys()).sort((a, b) => {
        const [, tA, sA] = a.split("|||");
        const [, tB, sB] = b.split("|||");
        if (tA.toLowerCase() < tB.toLowerCase()) return -1;
        if (tA.toLowerCase() > tB.toLowerCase()) return 1;
        if (sA.toLowerCase() < sB.toLowerCase()) return -1;
        if (sA.toLowerCase() > sB.toLowerCase()) return 1;
        return 0;
      });

      for (const key of groupKeys) {
        const g = groupsMap.get(key);
        const qRes = await axios.get(`${API}/api/repport/questions`, {
          params: { application: g.application, title: g.title, subtitle: g.subtitle },
        });
        const qs = qRes.data?.data?.questions || [];
        g.questions = qs.map((q) => ({
          id: q.id,
          question_text: q.question_text,
          question_type: q.question_type,
          answer: (answers[String(q.id)] ?? "").toString(),
        }));
      }

      setReportBuilt(groupKeys.map((k) => groupsMap.get(k)));
    } catch (e) {
      setReportError(t("repport_err_build_report"));
    } finally {
      setReportLoading(false);
    }
  };

  const openSubmissionModal = async (submission) => {
    setDetailModal(submission);
    await buildReportForSubmission(submission);
  };

  const closeModal = () => {
    setDetailModal(null);
    setReportLoading(false);
    setReportError("");
    setReportBuilt([]);
    setPdfLoading(false);
    setPdfError("");
  };

  const handleDownloadReportPdf = async () => {
    if (!detailModal || !reportPdfRef.current) return;
    setPdfError("");
    setPdfLoading(true);

    try {
      const printableNode = reportPdfRef.current;
      const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
        .map((node) => node.outerHTML)
        .join("\n");
      const printableHtml = printableNode.outerHTML;

      const win = window.open("", "_blank", "width=1024,height=900");
      if (!win) {
        setPdfError(t("repport_popup_blocked"));
        return;
      }

      const title = safeText(detailModal.serial_number, t("repport_pdf_title"));
      win.document.open();
      win.document.write(`<!doctype html>
<html lang="${document.documentElement.lang || "fr"}" dir="${document.documentElement.dir || "ltr"}">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${title}</title>
    ${styles}
    <style>
      @page { size: A4; margin: 10mm; }
      body { margin: 0; background: #fff !important; }
      .print-shell { max-width: 100%; margin: 0 auto; padding: 8px; }
      .print-shell * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    </style>
  </head>
  <body>
    <div class="print-shell">${printableHtml}</div>
    <script>
      window.onload = () => {
        setTimeout(() => {
          window.focus();
          window.print();
        }, 150);
      };
    </script>
  </body>
</html>`);
      win.document.close();
    } catch (e) {
      setPdfError(t("repport_err_pdf_download"));
    } finally {
      setPdfLoading(false);
    }
  };

  // ============= UI HELPERS =============
  const titleMsgClass =
    addTitleMessage.type === "error"
      ? "mt-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
      : "mt-4 rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-sm text-success";

  const subtitleMsgClass =
    addSubtitleMessage.type === "error"
      ? "mt-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
      : "mt-4 rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-sm text-success";

  const questionMsgClass =
    addQuestionMessage.type === "error"
      ? "mt-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
      : "mt-4 rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-sm text-success";

  const inputClass =
    "w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20";

  // ✅ filter + pagination
  const filteredHistory = useMemo(() => {
    const q = historyQuery.trim().toLowerCase();
    if (!q) return history;

    return history.filter((s) => {
      const username = getSubmissionUsername(s).toLowerCase();
      const qrId = getSubmissionQrValue(s).toLowerCase();
      const serial = String(s.serial_number ?? "").toLowerCase();
      const tech = String(s.tech_user ?? "").toLowerCase();

      return username.includes(q) || qrId.includes(q) || serial.includes(q) || tech.includes(q);
    });
  }, [history, historyQuery]);

  const totalHist = filteredHistory.length;
  const totalPagesHist = Math.max(1, Math.ceil(totalHist / LIMIT));
  const pageHistClamped = Math.min(pageHist, totalPagesHist - 1);
  const histStart = pageHistClamped * LIMIT;
  const histEnd = Math.min(totalHist, histStart + LIMIT);
  const pageHistory = filteredHistory.slice(histStart, histEnd);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-bold text-foreground">{t("repport_title")}</h1>

      <div className="mt-6 flex gap-1 rounded-lg bg-muted p-1" role="tablist" aria-label={t("repport_tabs_aria")}>
        <button onClick={() => setTab("manage")} className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${tab === "manage" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
          {t("repport_tab_manage")}
        </button>
        <button onClick={() => { setTab("history"); setHistoryQuery(""); fetchHistoryAll(); }} className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${tab === "history" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
          {t("repport_tab_history")}
        </button>
      </div>

      {tab === "manage" ? (
        <div className="mt-6 space-y-6">
          <div className="flex flex-col gap-2 sm:flex-row">
            <input type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} className={inputClass} placeholder={t("repport_title_ph")} disabled={addTitleLoading} />
            <button onClick={handleAddTitle} disabled={addTitleLoading} className="shrink-0 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60 sm:w-auto">{t("repport_add_title_btn")}</button>
          </div>
          {addTitleMessage.text && <p className={titleMsgClass}>{addTitleMessage.text}</p>}

          {loadingTitles ? (
            <p className="rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-primary">{t("repport_loading_titles")}</p>
          ) : titlesError ? (
            <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{titlesError}</p>
          ) : titles.length === 0 ? (
            <p className="rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-primary">{t("repport_no_titles")}</p>
          ) : (
            titles.map((title, index) => (
              <div key={title ?? `t-${index}`} className="rounded-xl border border-border bg-card p-6 shadow-sm transition-shadow hover:shadow-md">
                <div className="flex items-center justify-between">
                  <button onClick={() => toggleTitleOpen(title)} className="flex items-center gap-2 text-lg font-semibold text-foreground">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`transition-transform ${openTitle === title ? "rotate-180" : ""}`}>
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                    {title}
                  </button>
                  <button onClick={() => handleDeleteTitle(title)} className="text-xs text-destructive hover:underline">{t("delete")}</button>
                </div>

                {openTitle === title && (
                  <>
                    <div className="mt-4 space-y-4">
                      {loadingSubtitles ? (
                        <p className="rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-primary">{t("repport_loading_subtitles")}</p>
                      ) : subtitlesError ? (
                        <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{subtitlesError}</p>
                      ) : subtitles.length === 0 ? (
                        <p className="text-sm text-muted-foreground">{t("repport_no_subtitles")}</p>
                      ) : (
                        subtitles.map((sub, idx) => (
                          <div key={sub ?? `s-${idx}`} className="rounded-lg border border-border bg-muted/30 p-4">
                            <div className="flex items-center justify-between">
                              <button onClick={() => toggleSubtitleOpen(sub)} className="flex items-center gap-2 text-sm font-semibold text-foreground">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`transition-transform ${openSubtitle === sub ? "rotate-180" : ""}`}>
                                  <polyline points="6 9 12 15 18 9"/>
                                </svg>
                                {sub}
                              </button>
                              <div className="flex gap-2">
                                <button onClick={() => { toggleSubtitleOpen(sub); setShowAddQuestionForm(true); setAddQuestionMessage({ type: "", text: "" }); }} className="rounded-md border border-primary/20 px-2 py-1 text-xs text-primary hover:bg-primary/10">{t("repport_add_question_btn")}</button>
                                <button onClick={() => handleDeleteSubtitle(sub)} className="text-xs text-destructive hover:underline">{t("delete")}</button>
                              </div>
                            </div>

                            {openSubtitle === sub && (
                              <div className="mt-3">
                                {loadingQuestions ? (
                                  <p className="rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-primary">{t("repport_loading_questions")}</p>
                                ) : questionsError ? (
                                  <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{questionsError}</p>
                                ) : questions.length > 0 ? (
                                  <ul className="space-y-2">
                                    {questions.map((q) => (
                                      <li key={q.id} className="flex items-center justify-between rounded-md bg-card px-3 py-2 text-sm">
                                        <span className="text-foreground">{q.question_text} {q.is_required && <span className="text-destructive">*</span>} <span className="text-xs text-muted-foreground">({q.question_type})</span></span>
                                        <button onClick={() => handleDeleteQuestion(q.id)} className="text-xs text-destructive">×</button>
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="text-sm text-muted-foreground">{t("repport_no_questions")}</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>

                    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                      <input type="text" value={newSubtitle} onChange={(e) => setNewSubtitle(e.target.value)} className={inputClass} placeholder={t("repport_new_subtitle_placeholder")} disabled={addSubtitleLoading} />
                      <button onClick={handleAddSubtitle} disabled={addSubtitleLoading} className="shrink-0 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted sm:w-auto">{t("repport_add_subtitle_btn")}</button>
                    </div>
                    {addSubtitleMessage.text && <p className={subtitleMsgClass}>{addSubtitleMessage.text}</p>}
                  </>
                )}
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="mt-6">
          <input type="text" value={historyQuery} onChange={(e) => setHistoryQuery(e.target.value)} className={`${inputClass} mb-4`} placeholder={t("repport_filter_ph")} />

          {historyLoading ? (
            <p className="mb-4 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-primary">{t("repport_loading_history")}</p>
          ) : historyError ? (
            <p className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{historyError}</p>
          ) : (
            <>
              <div className="overflow-hidden rounded-xl border border-border bg-card">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("repport_pdf_date")}</th>
                      <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("repport_pdf_username")}</th>
                      <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("repport_pdf_technician")}</th>
                      <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("repport_pdf_qr_id")}</th>
                      <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageHistory.map((s) => (
                      <tr key={s.id} className="border-b border-border last:border-0">
                        <td className="px-4 py-3 text-muted-foreground">{formatIso(s.submitted_at, t)}</td>
                        <td className="px-4 py-3 text-foreground">{safeText(getSubmissionUsername(s), t("unknown"))}</td>
                        <td className="px-4 py-3 text-muted-foreground">{safeText(s.tech_user, t("unknown"))}</td>
                        <td className="px-4 py-3 text-muted-foreground">{safeText(getSubmissionQrValue(s), t("na"))}</td>
                        <td className="px-4 py-3"><button onClick={() => openSubmissionModal(s)} className="text-xs text-primary hover:underline">{t("view")}</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex justify-center gap-2">
                <button onClick={() => setPageHist((p) => Math.max(0, p - 1))} disabled={pageHistClamped === 0} className="rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground disabled:opacity-50">{t("prev")}</button>
              <span className="flex items-center px-3 text-sm text-muted-foreground">{pageHistClamped + 1} / {totalPagesHist}</span>
              <button onClick={() => setPageHist((p) => (p + 1 < totalPagesHist ? p + 1 : p))} disabled={pageHistClamped >= totalPagesHist - 1} className="rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground disabled:opacity-50">{t("next")}</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Detail modal */}
      {detailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 p-4" onClick={closeModal}>
          <div className="flex max-h-[92vh] w-full max-w-5xl flex-col rounded-2xl border border-border bg-card p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-foreground">{t("repport_details")}</h3>
              <button onClick={closeModal} className="rounded-lg p-1 text-muted-foreground hover:bg-muted">×</button>
            </div>
            <div className="mt-4 flex-1 overflow-y-auto pr-1">
              <div ref={reportPdfRef} className="rounded-lg p-1">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-muted-foreground">{t("repport_pdf_date")} :</span> <span className="font-medium text-foreground">{formatIso(detailModal.submitted_at, t)}</span></div>
                  <div><span className="text-muted-foreground">{t("repport_pdf_username")} :</span> <span className="font-medium text-foreground">{safeText(getSubmissionUsername(detailModal), t("na"))}</span></div>
                  <div><span className="text-muted-foreground">{t("repport_pdf_technician")} :</span> <span className="font-medium text-foreground">{safeText(detailModal.tech_user, t("na"))}</span></div>
                  <div><span className="text-muted-foreground">{t("repport_pdf_qr_id")} :</span> <span className="font-medium text-foreground">{safeText(getSubmissionQrValue(detailModal), t("na"))}</span></div>
                  <div className="col-span-2"><span className="text-muted-foreground">{t("repport_pdf_serial")} :</span> <span className="font-medium text-foreground">{safeText(detailModal.serial_number, t("na"))}</span></div>
                </div>

                <div className="mt-4 rounded-lg border border-border bg-muted/30 p-4">
                  <h4 className="text-sm font-semibold text-foreground">{t("repport_answer")}</h4>
                  {reportLoading ? (
                    <p className="mt-2 text-sm text-primary">{t("repport_building")}</p>
                  ) : reportError ? (
                    <p className="mt-2 text-sm text-destructive">{reportError}</p>
                  ) : reportBuilt.length > 0 ? (
                    <div className="mt-3 space-y-3">
                      {reportBuilt.map((group, index) => (
                        <div key={`${group.title}-${group.subtitle}-${index}`} className="rounded-md border border-border bg-card p-3">
                          <p className="text-sm font-semibold text-foreground">{safeText(group.title, t("na"))}</p>
                          <p className="text-xs text-muted-foreground">{safeText(group.subtitle, t("na"))}</p>
                          <div className="mt-2 overflow-hidden rounded-md border border-border">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-muted/50">
                                  <th className="px-3 py-2 text-start font-medium text-muted-foreground">{t("repport_th_question")}</th>
                                  <th className="px-3 py-2 text-start font-medium text-muted-foreground">{t("repport_th_answer")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.questions.map((q) => (
                                  <tr key={q.id} className="border-t border-border">
                                    <td className="px-3 py-2 text-foreground">
                                      {q.question_text}
                                      <span className="ml-1 text-xs text-muted-foreground">({q.question_type})</span>
                                    </td>
                                    <td className="px-3 py-2 font-medium text-foreground">
                                      {String(q.answer ?? "").trim() || t("repport_unanswered")}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : Object.entries(detailModal.answers || {}).length > 0 ? (
                    <div className="mt-2 space-y-2">
                      {Object.entries(detailModal.answers || {}).map(([key, val]) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{key}</span>
                          <span className="font-medium text-foreground">{String(val ?? "") || t("repport_unanswered")}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-muted-foreground">{t("repport_no_questions_in_submission")}</p>
                  )}
                </div>
              </div>
            </div>
            {pdfError && (
              <p className="mt-3 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {pdfError}
              </p>
            )}
            <div className="mt-4 flex flex-wrap justify-end gap-3">
              <button onClick={handleDownloadReportPdf} disabled={pdfLoading || reportLoading} className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-60">
                {pdfLoading ? t("repport_pdf_generating") : t("repport_download_pdf")}
              </button>
              <button onClick={closeModal} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">{t("close")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Add question modal */}
      {showAddQuestionForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 p-4">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-foreground">{t("repport_add_question_btn")}</h3>
            <div className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">{t("repport_question_text_ph")}</label>
                <input type="text" value={newQuestionText} onChange={(e) => setNewQuestionText(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">{t("repport_type")}</label>
                <select value={newQuestionType} onChange={(e) => setNewQuestionType(e.target.value)} className={inputClass}>
                  <option value="open">{t("repport_type_open")}</option>
                  <option value="qcm">{t("repport_type_qcm")}</option>
                  <option value="yes_no">{t("repport_type_yesno")}</option>
                </select>
              </div>
              {newQuestionType === "qcm" && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">{t("repport_qcm_options")}</label>
                  <input type="text" value={newQuestionOptionsText} onChange={(e) => setNewQuestionOptionsText(e.target.value)} className={inputClass} />
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" checked={newQuestionRequired} onChange={(e) => setNewQuestionRequired(e.target.checked)} />
                {t("required")}
              </label>
            </div>
            {addQuestionMessage.text && <p className={questionMsgClass}>{addQuestionMessage.text}</p>}
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowAddQuestionForm(false)} className="rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground">{t("cancel")}</button>
              <button onClick={handleAddQuestion} className="rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground">{t("save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RepportPage;
